import { useCallback, useEffect } from 'react';

import {
  type IStructuredLogBase,
  type IStructuredLogger,
  type TStructuredLogValidator,
} from '../types/structured-log-types';
import { structuredLogEventName } from '../utils/dispatch-structured-log-event';
import { handleStructuredLogEvent } from '../utils/handle-structured-log-event';
import { validateStructuredLog } from '../utils/validate-structured-log';

/**
 * `useStructuredLog` hooks listens for structured log events
 * @param logger - logger to log the structured log data or errors when `structuredLog` event is dispatched
 */
export const useStructuredLog = <T extends IStructuredLogBase>(
  logger: IStructuredLogger<T>,
  structuredLogValidator: TStructuredLogValidator<T> = validateStructuredLog,
) => {
  const structuredLogEventHandler = useCallback((event: Event) => {
    handleStructuredLogEvent<T>(event, logger, structuredLogValidator);
  }, [logger, structuredLogValidator]);

  useEffect(() => {
    window.addEventListener(structuredLogEventName, structuredLogEventHandler);

    return () => {
      window.removeEventListener(structuredLogEventName, structuredLogEventHandler);
    };
  }, [logger]);
};
